import React, { useState, useEffect } from 'react';
import {
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Box,
  Heading,
  useToast,
  Flex,
  Text,
  Breadcrumb,
  Spinner,
  BreadcrumbItem,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import ConfigImage from '../../components/ConfigImage';
import CredentialText from '../../components/CredentialText';
import FeatureSelect from '../../components/FeatureSelect';
import auth from '../../../../apis/auth';
import { AffiliateType, ConfigImageType, CredentialType } from '../../types';
import cms from '../../../../apis/cms';
import GeneralConfigValue from '../../components/GeneralConfigValue';

interface IUser {
  id: string;
  roles: string[];
}

const Configuration: React.FC = () => {
  const toast = useToast();
  const [user, setUser] = useState<IUser>({ id: '', roles: [] });
  const [features, setFeatures] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [configImages, setConfigImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  useEffect(() => {
    async function getImages() {
      setLoading(true);
      try {
        const { data: apiUser } = await auth.get<any>('users/me');
        setUser(apiUser);
        const { data } = await cms.rest.get<any>('/config-images', {
          params: {
            withFile: true,
          },
        });
        setConfigImages(data);
      } catch (err: any) {
        toast({
          title: 'Opss!!',
          description: err?.response?.data?.message || t('generalError'),
          status: 'error',
          position: 'top-right',
        });
      } finally {
        setLoading(false);
      }
    }
    getImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  async function handleRequests(index: number): Promise<void> {
    try {
      setLoading(true);
      if (index === 1 || index === 2) {
        const { data } = await auth.get<any>('affiliates', {
          params: {
            withCredentials: true,
          },
        });
        setAffiliates(data);
      }
      if (index === 2) {
        const { data } = await auth.get<any>('features');
        setFeatures(data);
      }
    } catch (err: any) {
      toast({
        title: 'Opss!!',
        description: err?.response?.data?.message || t('generalError'),
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setLoading(false);
    }
  }
  return (
    <Box
      p={8}
      w="100%"
      overflow="auto"
      bg="#F9F9F9"
      css={{
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          width: '6px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#469BFF',
          borderRadius: '24px',
        },
      }}
    >
      <Heading mb="24px" color="#469BFF" fontFamily="Poppins">
        {t('config')}
      </Heading>
      <Flex
        flexDir="column"
        bg="#F9F9F9"
        p={8}
        w="100%"
        borderRadius={6}
        boxShadow="lg"
      >
        <Tabs
          onChange={index => handleRequests(index)}
          marginLeft="1px"
          variant="line"
        >
          <TabList>
            <Tab>{t('general')}</Tab>
            {user.roles[0] === 'sysadmin' || user.roles[0] === 'admin' ? (
              <Tab>{t('providers')}</Tab>
            ) : (
              <></>
            )}
            {user.roles[0] === 'sysadmin' || user.roles[0] === 'admin' ? (
              <Tab>{t('features')}</Tab>
            ) : (
              <></>
            )}
          </TabList>

          <TabPanels mb={7}>
            <TabPanel>
              <Flex flexDir="row" w="fit-content" p={8} overflow="auto">
                {configImages.map((configImage: ConfigImageType) => (
                  <ConfigImage
                    title={configImage.name}
                    key={configImage.id}
                    nameImage={configImage.name}
                    url={configImage?.file?.url}
                    file={configImage?.file}
                  />
                ))}
              </Flex>
            </TabPanel>
            <TabPanel>
              <Flex flexDir="column" w="fit-content" p={8} overflow="auto">
                {affiliates.map((affiliate: AffiliateType) => (
                  <div key={affiliate.id}>
                    <Flex>
                      <Breadcrumb
                        separator=""
                        width="100%"
                        alignItems="flex-start"
                        sx={{
                          '.Pai:hover': {
                            '.teste': {
                              opacity: '1 !important',
                            },
                            '.Texto': {
                              transform: 'translateY(-20px) !important',
                            },
                          },
                        }}
                      >
                        {affiliate.credentials.length > 0 ? (
                          affiliate.credentials.map(
                            (credential: CredentialType) => (
                              <BreadcrumbItem
                                margin="0 10px 10px 0"
                                height="12rem"
                                width="10rem"
                                backgroundColor="#FFFFFF"
                                justifyContent="center"
                                className="Pai"
                              >
                                <Box>
                                  <Heading
                                    textAlign="center"
                                    pt="20px"
                                    size="lg"
                                    className="Texto"
                                    transition="all 0.3s ease"
                                    transform="translateY(20%)"
                                  >
                                    {affiliate.name.toUpperCase()}
                                    <Text fontSize="1rem" textColor="gray.500">
                                      {credential.slug}
                                    </Text>
                                  </Heading>
                                  <CredentialText
                                    style={{
                                      opacity: '0',
                                      transition: 'all 0.3s ease',
                                    }}
                                    className="teste"
                                    key={credential.slug}
                                    credential={credential}
                                    affiliate={affiliate}
                                  />
                                </Box>
                              </BreadcrumbItem>
                            ),
                          )
                        ) : (
                          <Text>{t('withoutCrendetialsForProvider')}</Text>
                        )}
                      </Breadcrumb>
                    </Flex>
                  </div>
                ))}
              </Flex>
            </TabPanel>
            <TabPanel>
              <Flex display="-ms-inline-flexbox" w="1080px" h="720px">
                <Flex width="650px">
                  <Box width="285px">
                    <GeneralConfigValue
                      label={t('shippingRule')}
                      name="free_shipping_value"
                      type="number"
                    />
                  </Box>
                  <Box width="285px" ml="50px">
                    <GeneralConfigValue
                      label={t('discountCoupon')}
                      name="discount_coupon"
                      type="text"
                    />
                  </Box>
                </Flex>
                <Flex
                  marginTop="40px"
                  w="1080px"
                  h="90%"
                  display="flex"
                  flexWrap="wrap"
                >
                  {features ? (
                    features.map(
                      (feature: {
                        id: string;
                        name: string;
                        affiliate_id: string;
                      }) => (
                        <Box width="285px" height="80px" marginRight="50px">
                          <FeatureSelect
                            title={feature.name}
                            options={affiliates}
                            selectValue={feature.affiliate_id}
                          />
                        </Box>
                      ),
                    )
                  ) : (
                    <Text>{t('notFindedFeatures')}</Text>
                  )}
                </Flex>
              </Flex>
            </TabPanel>
          </TabPanels>
        </Tabs>
        {loading && (
          <Flex
            position="absolute"
            align="center"
            justify="center"
            top="50%"
            left="50%"
          >
            <Spinner color="brand.500" size="xl" />
          </Flex>
        )}
      </Flex>
    </Box>
  );
};

export default Configuration;
